import type { ValueOf } from 'type-fest';

/**
 * Maps each auth status code emitted by `hotpads-api` in `AuthResponse.status`
 * to the i18n key under `useTranslations('auth')`. Single source of truth —
 * `AuthStatus` and `AUTH_STATUS_VALUES` are derived from this map.
 */
const STATUS_KEY_MAP = {
  INCORRECT_PASSWORD: 'errorIncorrectPassword',
  USERNAME_NOT_FOUND: 'errorUsernameNotFound',
  USER_ALREADY_EXISTS: 'errorAccountAlreadyExists',
  ACCOUNT_DEACTIVATED: 'errorAccountDeactivated',
  EMAIL_NOT_SENT: 'errorEmailNotSent',
  INTERNAL_ERROR: 'errorInternal',
  INVALID_API_CALL: 'errorInvalidApiCall',
  INVALID_CREDENTIALS: 'errorInvalidCredentials',
  EMAIL_NOT_SUPPLIED: 'errorEmailNotSupplied',
} as const;

export type AuthStatus = keyof typeof STATUS_KEY_MAP;

export const AUTH_STATUS_VALUES = Object.keys(STATUS_KEY_MAP) as Array<AuthStatus>;

export const FALLBACK_AUTH_MESSAGE_KEY = 'errorGeneric';
export const OAUTH_CALLBACK_MESSAGE_KEY = 'errorOAuthFailed';

// Magic-link verify failures get link-specific banner messages (the password-context
// STATUS_KEY_MAP copy reads wrong on the verify page). Keyed by the `/login?error=` value
// the verify route emits for the corresponding HpApiWebApiError reason.
const VERIFY_ERROR_KEY_MAP = {
  verify_link_used: 'errorVerifyLinkUsed',
  verify_link_invalid: 'errorVerifyLinkInvalid',
} as const;

export type AuthMessageKey =
  | (typeof STATUS_KEY_MAP)[AuthStatus]
  | ValueOf<typeof VERIFY_ERROR_KEY_MAP>
  | typeof FALLBACK_AUTH_MESSAGE_KEY
  | typeof OAUTH_CALLBACK_MESSAGE_KEY;

export function isAuthStatus(value: unknown): value is AuthStatus {
  return typeof value === 'string' && value in STATUS_KEY_MAP;
}

/**
 * Map a backend status string to the i18n key under `useTranslations('auth')`.
 * Unknown / null / undefined / empty inputs all collapse to the generic key so
 * callers can pass `response.status` directly without a guard.
 */
export function mapAuthStatusToMessageKey(status: string | null | undefined): AuthMessageKey {
  if (!isAuthStatus(status)) {
    return FALLBACK_AUTH_MESSAGE_KEY;
  }
  return STATUS_KEY_MAP[status];
}

// `/login?error=…` codes set by Route Handlers when sign-in fails before a
// session is established. `oauth_state` = tampered/expired OAuth state cookie;
// `oauth_failed` = OAuth upstream exchange threw; `verify_invalid` = magic-link
// missing params; `verify_failed` = magic-link upstream exchange threw or
// returned a non-loggedIn body. All four collapse to the same user-facing
// message — the user can only retry, not act differently per code.
const CALLBACK_ERROR_CODES = new Set([
  'oauth_failed',
  'oauth_state',
  'verify_failed',
  'verify_invalid',
]);

/**
 * Map a `/login?error=…` query value to an i18n key. Recognized callback codes
 * collapse to the generic OAuth/verify failure message; known backend statuses
 * route through their specific messages; unknown values return `null` (no
 * banner) and log a warning so future emitters that ship unmapped codes are
 * visible in ops rather than silently degrading to a generic error.
 */
export function mapOAuthCallbackErrorToMessageKey(
  error: string | null | undefined,
): AuthMessageKey | null {
  if (!error) return null;
  if (error in VERIFY_ERROR_KEY_MAP) return VERIFY_ERROR_KEY_MAP[error as keyof typeof VERIFY_ERROR_KEY_MAP];
  if (CALLBACK_ERROR_CODES.has(error)) return OAUTH_CALLBACK_MESSAGE_KEY;
  if (isAuthStatus(error)) return STATUS_KEY_MAP[error];
  console.warn('[Auth] unrecognized /login?error= value', { error });
  return null;
}
